import React from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import moment from "moment";
import {
  insurerColor,
  partName,
  partResultLines,
  partStatusMeta,
  shortLabel,
} from "../utils/policyParts";
import { friendlyErrorLine, partErrorContext } from "../utils/friendlyError";

const NAVY = "#1E3A5F";

const stamp = (d) => (d ? moment(d).format("DD MMM YYYY, hh:mm A") : "");

/** Everything worth knowing about one part, for its hover card. */
const PartTooltip = ({ part, meta, hideNumbers = false }) => {
  const row = (label, value) =>
    value ? (
      <Typography key={label} sx={{ fontSize: 11.5, lineHeight: 1.5 }}>
        {label}: <strong>{value}</strong>
      </Typography>
    ) : null;

  const done = ["completed", "completed_with_errors"].includes(part.status);
  const retryDue = part.status === "pending" && part.nextRetryAt && new Date(part.nextRetryAt) > new Date();

  return (
    <Box sx={{ py: 0.25, maxWidth: 320 }}>
      <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
        {partName(part)} — {meta.longLabel}
      </Typography>
      {part.kind === "withPart" && part.mirrors && (
        <Typography sx={{ fontSize: 11.5, opacity: 0.85 }}>
          Bought together with {shortLabel(part.mirrors)}
        </Typography>
      )}
      {meta.hint && <Typography sx={{ fontSize: 11.5, opacity: 0.85 }}>{meta.hint}</Typography>}
      {part.kind !== "withPart" && part.maxAttempts
        ? row("Attempts", `${part.attempts || 0} / ${part.maxAttempts}`)
        : null}
      {!done && row("Stage", part.stage)}
      {retryDue && row("Next retry", stamp(part.nextRetryAt))}
      {done && row("Completed", stamp(part.completedAt))}
      {!done && !retryDue && part.status !== "pending" && part.status !== "processing" && row("Failed", stamp(part.failedAt))}
      {!hideNumbers && partResultLines(part).map(([label, value]) => row(label, value))}
      {part.result?.warning && (
        <Typography sx={{ fontSize: 11.5, mt: 0.25 }}>Warning: {part.result.warning}</Typography>
      )}
      {part.routingChanged && (
        <Typography sx={{ fontSize: 11.5, mt: 0.25 }}>
          Insurer changed after purchase: {part.routingChanged.from || "—"} →{" "}
          {part.routingChanged.to || "—"} (already bought with {part.routingChanged.from || "the old insurer"})
        </Typography>
      )}
      {part.lastError && (
        <Typography
          sx={{
            fontSize: 11.5,
            mt: 0.5,
            color: "#FDA29B",
            display: "-webkit-box",
            WebkitLineClamp: 4,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            wordBreak: "break-word",
          }}
        >
          {friendlyErrorLine(part.lastError, partErrorContext(part))}
        </Typography>
      )}
    </Box>
  );
};

/** One status line: dot, bold part label, insurer, status — ellipsised. */
export const PartLine = ({ part, maxWidth = 230, hideNumbers = false }) => {
  const meta = partStatusMeta(part);
  const insurer = insurerColor(part.company).main;
  const sep = (
    <Box component="span" sx={{ color: "#98A2B3", mx: "4px" }}>
      ·
    </Box>
  );

  return (
    <Tooltip title={<PartTooltip part={part} meta={meta} hideNumbers={hideNumbers} />} arrow placement="top">
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          maxWidth,
          minWidth: 0,
          lineHeight: "16px",
          cursor: "default",
        }}
      >
        <Box
          sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: meta.color, flexShrink: 0 }}
        />
        <Box
          component="span"
          sx={{
            fontSize: 12,
            lineHeight: "16px",
            minWidth: 0,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          <Box component="span" sx={{ fontWeight: 700, color: NAVY }}>
            {part.label || shortLabel(part.key)}
          </Box>
          {part.company ? (
            <>
              {sep}
              <Box component="span" sx={{ color: insurer, fontWeight: 600 }}>
                {part.company}
              </Box>
            </>
          ) : null}
          {sep}
          <Box component="span" sx={{ color: meta.color, fontWeight: 600 }}>
            {meta.label}
          </Box>
        </Box>
      </Box>
    </Tooltip>
  );
};

/**
 * The table's Status column when a policy has real parts: one compact line per
 * part instead of one chip. `children` is whatever caption goes underneath
 * ("payment link · can re-submit", "automation paused"). `hideNumbers` leaves the
 * bought numbers out of each part's hover card — the table shows them once, in
 * its Policy / Proposal column.
 */
const PartStatusList = ({ parts = [], maxWidth = 230, children = null, hideNumbers = false }) => (
  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "2px" }}>
    {parts
      .filter((p) => p.status !== "cancelled")
      .map((part) => (
        <PartLine key={part.key} part={part} maxWidth={maxWidth} hideNumbers={hideNumbers} />
      ))}
    {children}
  </Box>
);

export default PartStatusList;
